import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { PROPERTIES, VIEW_SHARES_SEED, VIEWS_SEED } from '../data';
import type {
  CreateViewInput,
  TagTypeKey,
  UpdateViewInput,
  View,
  ViewMatchMode,
  ViewPage,
  ViewQuery,
  ViewShare,
  ViewShareGrant,
  ViewVisibility,
} from '../types';
import { CurrentUserService } from './current-user.service';
import { TagService } from './tag.service';

const LIST_LATENCY_MS = 400;

type ViewSortValue = string | number | null | undefined;

@Injectable({ providedIn: 'root' })
export class ViewService {
  private readonly tagService = inject(TagService);

  private readonly currentUser = inject(CurrentUserService);

  get currentUserId(): string {
    return this.currentUser.id();
  }

  private readonly _views = signal<ReadonlyArray<View>>(VIEWS_SEED);
  readonly views = this._views.asReadonly();

  private readonly _shares = signal<ReadonlyArray<ViewShare>>(VIEW_SHARES_SEED);
  readonly shares = this._shares.asReadonly();

  private readonly _revision = signal<number>(0);
  readonly revision = this._revision.asReadonly();

  readonly visibleViews = computed<ReadonlyArray<View>>(() =>
    this._views().filter((view) => this.isVisible(view)),
  );

  viewById(id: string): View | null {
    return this._views().find((view) => view.id === id) ?? null;
  }

  sharedUserIds(viewId: string): ReadonlyArray<string> {
    return this._shares()
      .filter((share) => share.viewId === viewId)
      .map((share) => share.userId);
  }

  sharesForView(viewId: string): ReadonlyArray<ViewShare> {
    return this._shares().filter((share) => share.viewId === viewId);
  }

  isVisible(view: View): boolean {
    if (view.visibility !== 'private') return true;
    if (view.ownerId === this.currentUserId) return true;
    return this.sharedUserIds(view.id).includes(this.currentUserId);
  }

  isEditable(view: View): boolean {
    if (view.visibility === 'internal') return false;
    if (view.visibility === 'organisation') return true;
    return view.ownerId === this.currentUserId;
  }

  resolveObjectIds(view: View): ReadonlyArray<string> {
    return this.resolve(view.tagIds, view.matchMode, view.typeKey);
  }

  resolveAccountIds(view: View): ReadonlyArray<string> {
    return this.resolve(view.tagIds, view.matchMode, 'account');
  }

  previewObjectIds(
    tagIds: ReadonlyArray<string>,
    matchMode: ViewMatchMode,
    typeKey: TagTypeKey,
  ): ReadonlyArray<string> {
    return this.resolve(tagIds, matchMode, typeKey);
  }

  private resolve(
    tagIds: ReadonlyArray<string>,
    matchMode: ViewMatchMode,
    objectType: TagTypeKey,
  ): ReadonlyArray<string> {
    const tags = tagIds
      .map((id) => this.tagService.tagById(id))
      .filter((tag): tag is NonNullable<typeof tag> => tag != null)
      .filter((tag) => this.tagService.category(tag.categoryId)?.typeKey === objectType);
    if (tags.length === 0) {
      return objectType === 'account' ? PROPERTIES.map((account) => account.id) : [];
    }

    if (matchMode === 'any') {
      const union = new Set<string>();
      for (const tag of tags) {
        for (const id of this.tagService.objectIdsForTag(tag.id, objectType)) union.add(id);
      }
      return [...union];
    }

    const byCategory = new Map<string, Set<string>>();
    for (const tag of tags) {
      const set = byCategory.get(tag.categoryId) ?? new Set<string>();
      for (const id of this.tagService.objectIdsForTag(tag.id, objectType)) set.add(id);
      byCategory.set(tag.categoryId, set);
    }

    const groups = [...byCategory.values()];
    let intersection: Set<string> | null = null;
    for (const group of groups) {
      if (intersection === null) {
        intersection = new Set(group);
        continue;
      }
      intersection = new Set([...intersection].filter((id) => group.has(id)));
    }
    return intersection ? [...intersection] : [];
  }

  createView(input: CreateViewInput): View {
    const view: View = {
      id: this.nextId('view'),
      name: input.name.trim(),
      description: input.description.trim(),
      typeKey: input.typeKey,
      visibility: input.visibility,
      matchMode: input.matchMode,
      tagIds: [...input.tagIds],
      ownerId: this.currentUserId,
      createdAt: this.todayIso(),
    };
    this._views.update((views) => [...views, view]);
    this.replaceShares(view.id, view.visibility, input.shares);
    this.bump();
    return view;
  }

  updateView(id: string, input: UpdateViewInput): View | null {
    const existing = this.viewById(id);
    if (!existing) return null;
    const updated: View = {
      ...existing,
      name: input.name.trim(),
      description: input.description.trim(),
      visibility: input.visibility,
      matchMode: input.matchMode,
      tagIds: [...input.tagIds],
    };
    this._views.update((views) => views.map((view) => (view.id === id ? updated : view)));
    this.replaceShares(id, updated.visibility, input.shares);
    this.bump();
    return updated;
  }

  deleteView(id: string): void {
    this._views.update((views) => views.filter((view) => view.id !== id));
    this._shares.update((shares) => shares.filter((share) => share.viewId !== id));
    this.bump();
  }

  list(query: ViewQuery): Observable<ViewPage> {
    let result = this.visibleViews().slice();

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (view) =>
          view.name.toLowerCase().includes(term) ||
          view.description.toLowerCase().includes(term),
      );
    }

    const visibilityFilters = query.filters['visibility'];
    if (visibilityFilters?.length) {
      const allowed = new Set(visibilityFilters);
      result = result.filter((view) => allowed.has(view.visibility));
    }

    const sortField = query.sortField;
    if (sortField) {
      const accessor = this.sortAccessor(sortField);
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => this.compare(accessor(a), accessor(b)) * direction);
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const data = result.slice(start, start + Math.max(1, query.rows));

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  private sortAccessor(field: string): (view: View) => ViewSortValue {
    switch (field) {
      case 'visibility':
        return (view) => view.visibility;
      case 'tagCount':
        return (view) => view.tagIds.length;
      case 'objectCount':
        return (view) => this.resolveObjectIds(view).length;
      case 'createdAt':
        return (view) => view.createdAt;
      default:
        return (view) => view.name.toLowerCase();
    }
  }

  private compare(a: ViewSortValue, b: ViewSortValue): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  private replaceShares(
    viewId: string,
    visibility: ViewVisibility,
    grants: ReadonlyArray<ViewShareGrant>,
  ): void {
    const others = this._shares().filter((share) => share.viewId !== viewId);
    if (visibility !== 'private') {
      this._shares.set(others);
      return;
    }
    const next: ViewShare[] = grants
      .filter((grant) => grant.userId !== this.currentUserId)
      .map((grant) => ({
        viewId,
        userId: grant.userId,
        canEdit: grant.canEdit,
        canShare: grant.canShare,
      }));
    this._shares.set([...others, ...next]);
  }

  private bump(): void {
    this._revision.update((n) => n + 1);
  }

  private nextId(prefix: string): string {
    const rand = Math.random().toString(36).slice(2, 8);
    return `${prefix}-${Date.now().toString(36)}-${rand}`;
  }

  private todayIso(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
